import React, { useContext } from 'react';
import { Tag, Typography } from 'antd';
import { BankOutlined, CheckCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { EModule } from '../../../enums';
import { IInvoiceDetailsDividerTicket } from '../interfaces';
import { convertMoedaBRDoJSNovo, formatDateStringToDate, formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface DividerTicketsCardProps {
  regraVigente: IInvoiceDetailsDividerTicket | undefined;
  modules?: EModule[];
  highlighted?: boolean;
  dataNota: string | null;
}

const RuleField: React.FC<{ label: string; children: React.ReactNode; isDark: boolean }> = ({
  label,
  children,
  isDark,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderBottom: `1px dashed ${isDark ? '#333333' : '#e2e8f0'}`,
      }}
    >
      <Text style={{ fontSize: 12.5, color: isDark ? '#94a3b8' : '#64748b' }}>
        {label}
      </Text>
      <Text style={{ fontSize: 13, fontWeight: 600, textAlign: 'right', color: isDark ? '#f1f5f9' : '#1e293b' }}>
        {children}
      </Text>
    </div>
  );
};

function renderValorOuIlimitado(value?: number | null, isUnlimited?: boolean): React.ReactNode {
  if (isUnlimited) return <Tag color="gold" style={{ margin: 0, fontWeight: 600 }}>Ilimitado</Tag>;
  if (value === null || value === undefined) return '--';
  return "R$ " + convertMoedaBRDoJSNovo(value);
}

export const DividerTicketsCard: React.FC<DividerTicketsCardProps> = ({
  regraVigente,
  modules,
  highlighted = false,
  dataNota,
}) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  if (!regraVigente) {
    return <></>;
  }

  const cardBg = isDark ? '#141414' : '#ffffff';
  const itemBg = isDark ? '#1f1f1f' : '#f8fafc';
  const itemBorder = isDark ? '#2a2a2a' : '#eaedf1';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(22, 119, 255, 0.15)' : 'rgba(22, 119, 255, 0.1)',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <BankOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Divisor de Bilhetes Vigente
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Regra do município na data da nota ({dataNota ? formatDateStringToDate(dataNota) : '--'})
            </div>
          </div>
        </div>

        {highlighted && (
          <Tag color="success" icon={<CheckCircleFilled />} style={{ margin: 0, fontWeight: 600 }}>
            Regra utilizada
          </Tag>
        )}
      </div>

      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        <div
          style={{
            background: itemBg,
            borderRadius: 8,
            border: `1px solid ${itemBorder}`,
            padding: '12px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <div style={{ fontSize: 11, color: labelColor }}>Início da Vigência</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>
              {formatDateStringToDateAndTime(regraVigente.start_date)}
            </div>
          </div>

          {regraVigente.description && (
            <div style={{ fontSize: 12, color: labelColor, display: 'flex', alignItems: 'center', gap: 4 }}>
              <InfoCircleOutlined style={{ color: '#1677ff' }} />
              <span>{regraVigente.description}</span>
            </div>
          )}
        </div>
        {modules?.includes(EModule.INVOICE_NFC) && (
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1677ff', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Nota Fiscal de Consumidor (NFC-e)
            </div>
            <RuleField label="Valor por bilhete" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_nfc, false)}
            </RuleField>
            <RuleField label="Valor máximo considerado" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_max_nfc, regraVigente.is_unlimited_nfc)}
            </RuleField>
            <RuleField label="Valor máximo por CNPJ" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.max_amount_by_cnpj_nfc, regraVigente.is_unlimited_by_cnpj_nfc)}
            </RuleField>
          </div>
        )}

        {modules?.includes(EModule.INVOICE_NFS) && (
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#722ed1', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Nota Fiscal de Serviço (NFS-e)
            </div>
            <RuleField label="Valor por bilhete" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_nfs, false)}
            </RuleField>
            <RuleField label="Valor máximo considerado" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_max_nfs, regraVigente.is_unlimited_nfs)}
            </RuleField>
            <RuleField label="Valor máximo por CNPJ" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.max_amount_by_cnpj_nfs, regraVigente.is_unlimited_by_cnpj_nfs)}
            </RuleField>
          </div>
        )}

        {modules?.includes(EModule.TAXES) && (
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#fa8c16', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Impostos Municipais (IPTU / Taxas)
            </div>
            <RuleField label="Valor da taxa" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_taxe, false)}
            </RuleField>
            <RuleField label="Valor máximo da taxa" isDark={isDark}>
              {renderValorOuIlimitado(regraVigente.value_max_taxe, regraVigente.is_unlimited_taxe)}
            </RuleField>
          </div>
        )}

        {modules?.includes(EModule.TAXES_AUTO) && (
          <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '14px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#13c2c2', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Imposto (Importação Automática)
            </div>
            <RuleField label="Bilhetes por pagamento à vista" isDark={isDark}>
              {regraVigente.tickets_from_full_tax_payment ?? '--'}
            </RuleField>
            <RuleField label="Bilhetes por pagamento parcelado" isDark={isDark}>
              {regraVigente.tickets_from_partial_tax_payment ?? '--'}
            </RuleField>
          </div>
        )}

        <div style={{ background: itemBg, borderRadius: 8, border: `1px solid ${itemBorder}`, padding: '14px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: textColor, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
            Regras Gerais
          </div>
          <RuleField label="Gera bilhete na 1ª nota importada" isDark={isDark}>
            <Tag color={regraVigente.generate_ticket_on_first_invoice_import ? 'green' : 'default'} style={{ margin: 0, fontWeight: 600 }}>
              {regraVigente.generate_ticket_on_first_invoice_import ? 'Sim' : 'Não'}
            </Tag>
          </RuleField>
          <RuleField label="Criada em" isDark={isDark}>
            {formatDateStringToDateAndTime(regraVigente.created_at)}
          </RuleField>
        </div>
      </div>
    </div>
  );
};

export default DividerTicketsCard;

